// ─────────────────────────────────────────────────────────────────────────
// Hearth — reconnecting WebSocket hook for the live play surface.
// Centralizes: connection lifecycle, full-game state, snapshot, the fused
// narration stream (log entries + inline tool beats), and the "thinking"
// indicator. Exposes a typed send() for client→server actions.
// ─────────────────────────────────────────────────────────────────────────
import { useCallback, useEffect, useRef, useState } from "react";
import { api, getAuthToken } from "../lib/api";
import { uid } from "../lib/format";
import { useToast } from "./useToast";
import type {
  AuthUser,
  FullGame,
  LogEntry,
  Snapshot,
  StreamItem,
  WsClientMessage,
  WsEvent,
} from "../lib/types";

export type ConnState = "connecting" | "open" | "closed" | "reconnecting";

// Most recent entries pulled on first hydration (and the cap on any single
// backfill fetch after a long outage).
const LOG_FETCH_MAX = 500;
// Backfill refetches a few already-seen entries so a miscounted local length
// can't leave a hole; the merge dedups by entry id.
const LOG_OVERLAP = 5;
// Identical game-wide error messages inside this window are dropped (the
// server broadcasts action errors to every client, not just the actor).
const ERROR_DEDUP_MS = 5_000;

export interface ThinkingState {
  who: string;
  role: "dm" | "player";
}

// A "your turn" / "roll called" nudge for a character THIS client controls. `nonce`
// changes on every fire so the UI can re-trigger its entrance animation even when the
// text repeats. Consumed by a `.turn-toast`.
export interface TurnToast {
  text: string;
  nonce: number;
}

// Short two-note WebAudio chime — self-contained, no asset/dep. Off by default; only
// played when the user has opted in. Swallows all failures (autoplay policy, no
// AudioContext) so a missing chime can never break the turn nudge.
function playTurnChime(): void {
  try {
    const AC: typeof AudioContext =
      window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    if (!AC) return;
    const ctx = new AC();
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.connect(g);
    g.connect(ctx.destination);
    o.type = "sine";
    o.frequency.setValueAtTime(880, ctx.currentTime);
    o.frequency.setValueAtTime(1174.7, ctx.currentTime + 0.13);
    g.gain.setValueAtTime(0.0001, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.14, ctx.currentTime + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.38);
    o.start();
    o.stop(ctx.currentTime + 0.4);
    o.onended = () => { try { ctx.close(); } catch { /* already closed */ } };
  } catch {
    /* no audio available — the toast + title flash still fire */
  }
}

// Softer single-note tick for EVERY new beat (a turn passed / new narration to read),
// distinct from the fuller two-note your-turn chime above. Quiet + short so a long
// session doesn't get grating. Same self-contained WebAudio, swallows all failures.
function playBeatTick(): void {
  try {
    const AC: typeof AudioContext =
      window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    if (!AC) return;
    const ctx = new AC();
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.connect(g);
    g.connect(ctx.destination);
    o.type = "sine";
    o.frequency.setValueAtTime(740, ctx.currentTime);
    g.gain.setValueAtTime(0.0001, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.08, ctx.currentTime + 0.015);
    g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.22);
    o.start();
    o.stop(ctx.currentTime + 0.24);
    o.onended = () => { try { ctx.close(); } catch { /* already closed */ } };
  } catch {
    /* no audio available */
  }
}

const CHIME_KEY = "hearth:chime";

export interface ChatMessage {
  uid: string;
  who: string;
  userId: string | null;
  text: string;
  ts: number;
}

export interface UseGameSocket {
  conn: ConnState;
  game: FullGame | null;
  snapshot: Snapshot | null;
  stream: StreamItem[];
  thinking: ThinkingState | null;
  error: string | null;
  you: AuthUser | null;
  present: { id: string; display_name: string }[];
  chat: ChatMessage[];
  art: Record<string, string>;
  /** True when the table no longer exists server-side (deleted / bad id). */
  gone: boolean;
  clearError: () => void;
  send: (msg: WsClientMessage) => boolean;
  reconnect: () => void;
  /** Refetch the full game (seats, characters, meta) out of band. */
  refreshGame: () => void;
  /** Fires when the turn pointer lands on — or a roll is called for — a PC this
   *  client controls. Render as a `.turn-toast`. null when nothing pending. */
  turnToast: TurnToast | null;
  /** Dismiss the current turn nudge (also stops the tab-title flash). */
  dismissTurnToast: () => void;
  /** User opt-in for the turn chime (persisted; off by default). */
  chimeEnabled: boolean;
  setChimeEnabled: (on: boolean) => void;
}

function wsUrl(gameId: string): string {
  const proto = window.location.protocol === "https:" ? "wss" : "ws";
  const token = getAuthToken();
  const q = token ? `?token=${encodeURIComponent(token)}` : "";
  return `${proto}://${window.location.host}/ws/${gameId}${q}`;
}

export function useGameSocket(gameId: string | null): UseGameSocket {
  const [conn, setConn] = useState<ConnState>("connecting");
  const [game, setGame] = useState<FullGame | null>(null);
  const [snapshot, setSnapshot] = useState<Snapshot | null>(null);
  const [stream, setStream] = useState<StreamItem[]>([]);
  const [thinking, setThinking] = useState<ThinkingState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [you, setYou] = useState<AuthUser | null>(null);
  const [present, setPresent] = useState<{ id: string; display_name: string }[]>([]);
  const [chat, setChat] = useState<ChatMessage[]>([]);
  const [gone, setGone] = useState(false);
  // Live art map: key = `${kind}:${id}` (id = character_id for characters, name for
  // item/spell, "scene" for the scene). Populated by WS "art" broadcasts.
  const [art, setArt] = useState<Record<string, string>>({});
  // Turn nudge for a PC this client controls (your-turn / roll-called).
  const [turnToast, setTurnToast] = useState<TurnToast | null>(null);
  const [chimeEnabled, setChimeEnabledState] = useState<boolean>(() => {
    // Default ON — a new-beat notification sound is wanted out of the box; only off
    // when the user has explicitly muted it (stored "0").
    try { return window.localStorage.getItem(CHIME_KEY) !== "0"; } catch { return true; }
  });
  const { push: pushToast } = useToast();

  const wsRef = useRef<WebSocket | null>(null);
  const retryRef = useRef(0);
  const reconnectTimer = useRef<number | null>(null);
  const closedByUs = useRef(false);
  const fatalRef = useRef(false);
  const seenLogIds = useRef<Set<string>>(new Set());
  // Server log length we've caught up to; drives gap detection + backfill.
  const knownLogLen = useRef(0);
  const logFetchBusy = useRef(false);
  const lastErrorRef = useRef<{ msg: string; ts: number } | null>(null);
  const thinkingTimeout = useRef<number | null>(null);
  // Entry ids whose DM prose is arriving as narration_delta chunks — a provisional,
  // in-place log item grows until the matching 'log' event finalizes it.
  const streamingIds = useRef<Set<string>>(new Set());
  // Edge-detection for the turn nudge: the last combatant id we saw take the current
  // turn, and the pending-roll ids we've already nudged for (so we fire once each).
  const lastTurnCharRef = useRef<string | null>(null);
  const notifiedRollsRef = useRef<Set<string>>(new Set());
  // Edge-detection for the conductor spotlight (out of combat): the last `awaiting`
  // character id we saw, so a fresh baton handed to one of OURS fires the nudge once.
  const lastAwaitingRef = useRef<string | null>(null);
  // Tab-title flash bookkeeping ({ interval id, original title }); null when idle.
  const titleFlashRef = useRef<{ iv: number; orig: string; stop: () => void } | null>(null);
  const turnToastTimer = useRef<number | null>(null);
  const chimeRef = useRef(chimeEnabled);
  chimeRef.current = chimeEnabled;
  // Debounce the per-beat tick so a burst of entries (streaming, several beats at
  // once) rings once, not a machine-gun.
  const lastTickRef = useRef(0);

  const clearError = useCallback(() => setError(null), []);

  const setChimeEnabled = useCallback((on: boolean) => {
    setChimeEnabledState(on);
    try { window.localStorage.setItem(CHIME_KEY, on ? "1" : "0"); } catch { /* private mode */ }
  }, []);

  // Stop the tab-title flash and restore the page's original title.
  const stopTitleFlash = useCallback(() => {
    const f = titleFlashRef.current;
    if (!f) return;
    window.clearInterval(f.iv);
    try { document.title = f.orig; } catch { /* ignore */ }
    window.removeEventListener("focus", f.stop);
    titleFlashRef.current = null;
  }, []);

  const dismissTurnToast = useCallback(() => {
    setTurnToast(null);
    if (turnToastTimer.current) { window.clearTimeout(turnToastTimer.current); turnToastTimer.current = null; }
    stopTitleFlash();
  }, [stopTitleFlash]);

  // Fire a turn nudge: a `.turn-toast`, a flashing tab title (so a backgrounded tab
  // catches the eye), and an optional chime. Auto-clears after a few seconds.
  const fireTurnNudge = useCallback((text: string) => {
    setTurnToast({ text, nonce: Date.now() });
    if (turnToastTimer.current) window.clearTimeout(turnToastTimer.current);
    turnToastTimer.current = window.setTimeout(() => setTurnToast(null), 6500);
    // Flash the tab title until the tab regains focus (or a 20s safety cap).
    if (!titleFlashRef.current) {
      const orig = document.title;
      let on = false;
      const stop = () => stopTitleFlash();
      const iv = window.setInterval(() => {
        on = !on;
        try { document.title = on ? "🎲 YOUR TURN — Hearth" : orig; } catch { /* ignore */ }
      }, 900);
      titleFlashRef.current = { iv, orig, stop };
      window.addEventListener("focus", stop, { once: true });
      window.setTimeout(stop, 20_000);
    }
    if (chimeRef.current) playTurnChime();
  }, [stopTitleFlash]);

  const refreshGame = useCallback(() => {
    if (!gameId) return;
    api.game(gameId).then(setGame).catch(() => { /* transient; WS still live */ });
  }, [gameId]);

  const appendLog = useCallback((entry: LogEntry) => {
    if (entry.id && seenLogIds.current.has(entry.id)) return;
    if (entry.id) seenLogIds.current.add(entry.id);
    knownLogLen.current += 1;
    // If this entry's prose was streamed in as narration_delta chunks, a provisional
    // item already exists — finalize it IN PLACE (keep its uid so React reconciles the
    // growing bubble instead of remounting) and swap the partial text for the authoritative
    // final entry. Otherwise append fresh.
    const wasStreaming = entry.id ? streamingIds.current.delete(entry.id) : false;
    setStream((prev) => {
      if (wasStreaming) {
        const idx = prev.findIndex((it) => it.kind === "log" && it.entry.id === entry.id);
        if (idx !== -1) {
          const item: StreamItem = { kind: "log", uid: prev[idx].uid, entry };
          return [...prev.slice(0, idx), item, ...prev.slice(idx + 1)];
        }
      }
      return [...prev, { kind: "log", uid: uid("log"), entry }];
    });
  }, []);

  // Merge fetched history into the stream, keeping chronology: entries land
  // before any live entry newer than the batch, so a fetch that races live
  // events can't reorder the story. Dedups by entry id.
  const mergeHistory = useCallback((entries: LogEntry[]) => {
    const fresh = entries.filter((e) => e.id && !seenLogIds.current.has(e.id));
    if (fresh.length === 0) return;
    fresh.forEach((e) => seenLogIds.current.add(e.id));
    const lastTs = fresh[fresh.length - 1].ts ?? 0;
    setStream((prev) => {
      const items: StreamItem[] = fresh.map((e) => ({ kind: "log", uid: uid("log"), entry: e }));
      const at = prev.findIndex((it) => it.kind === "log" && (it.entry.ts ?? 0) > lastTs);
      if (at === -1) return [...prev, ...items];
      return [...prev.slice(0, at), ...items, ...prev.slice(at)];
    });
  }, []);

  // Fetch the log tail we haven't seen yet. First connect loads the most
  // recent LOG_FETCH_MAX entries; a reconnect fetches just the gap missed
  // while offline. `total` is the server's current log length.
  const backfillLog = useCallback((total: number) => {
    if (!gameId || total <= 0 || logFetchBusy.current) return;
    logFetchBusy.current = true;
    let offset = Math.max(0, Math.min(knownLogLen.current, total) - LOG_OVERLAP);
    if (total - offset > LOG_FETCH_MAX) offset = total - LOG_FETCH_MAX;
    api.log(gameId, offset, total - offset)
      .then((res) => {
        mergeHistory(res.entries);
        knownLogLen.current = Math.max(knownLogLen.current, res.total);
      })
      .catch(() => { /* non-fatal; the next state event re-triggers */ })
      .finally(() => { logFetchBusy.current = false; });
  }, [gameId, mergeHistory]);

  // Track the server log length from an init/state event, backfilling any gap
  // (or syncing down after an undo shrinks the log).
  const syncLogLen = useCallback((logLen: number) => {
    if (logLen > knownLogLen.current) backfillLog(logLen);
    else if (logLen < knownLogLen.current) knownLogLen.current = logLen;
  }, [backfillLog]);

  const connect = useCallback(() => {
    if (!gameId || fatalRef.current) return;
    if (reconnectTimer.current) {
      window.clearTimeout(reconnectTimer.current);
      reconnectTimer.current = null;
    }
    // Never let two live sockets coexist: detach the old one's handlers before
    // closing so its async onclose can't clobber wsRef or schedule a rival
    // reconnect against the fresh connection.
    const prev = wsRef.current;
    if (prev) {
      prev.onopen = null;
      prev.onmessage = null;
      prev.onerror = null;
      prev.onclose = null;
      try { prev.close(); } catch { /* already closed */ }
      wsRef.current = null;
    }
    closedByUs.current = false;
    setConn(retryRef.current === 0 ? "connecting" : "reconnecting");

    let ws: WebSocket;
    try {
      ws = new WebSocket(wsUrl(gameId));
    } catch (e) {
      setConn("closed");
      setError(`Could not open connection: ${String(e)}`);
      return;
    }
    wsRef.current = ws;

    ws.onopen = () => {
      retryRef.current = 0;
      setConn("open");
    };

    ws.onmessage = (ev) => {
      let data: WsEvent;
      try {
        data = JSON.parse(ev.data as string) as WsEvent;
      } catch {
        return;
      }
      // Streaming DM prose (C3): {type:"narration_delta", entry_id, text}. Not in the
      // WsEvent union yet, so narrow locally and handle before the typed switch. Grow a
      // provisional DM log item in place; the matching 'log' event finalizes it (appendLog
      // replaces the partial with the authoritative entry, deduped by id).
      const raw = data as unknown as { type: string; entry_id?: string; text?: string };
      if (raw.type === "narration_delta") {
        const entryId = raw.entry_id;
        const chunk = raw.text;
        if (!entryId || !chunk) return;
        setThinking(null);
        if (thinkingTimeout.current) window.clearTimeout(thinkingTimeout.current);
        streamingIds.current.add(entryId);
        setStream((prev) => {
          const idx = prev.findIndex((it) => it.kind === "log" && it.entry.id === entryId);
          if (idx === -1) {
            const entry: LogEntry = {
              id: entryId, ts: Date.now() / 1000, kind: "dm", speaker: "DM",
              speaker_id: null, text: chunk, data: { streaming: true },
            };
            return [...prev, { kind: "log", uid: uid("log"), entry }];
          }
          const it = prev[idx] as Extract<StreamItem, { kind: "log" }>;
          const entry: LogEntry = { ...it.entry, text: it.entry.text + chunk, data: { ...it.entry.data, streaming: true } };
          return [...prev.slice(0, idx), { ...it, entry }, ...prev.slice(idx + 1)];
        });
        return;
      }
      switch (data.type) {
        case "init": {
          setGame(data.game);
          setSnapshot(data.game.snapshot);
          setYou(data.you ?? null);
          // Hydrate the narration stream from server-side history (the init
          // payload carries only log_len, not the entries). Runs on every
          // init so a reconnect backfills whatever was missed while offline.
          syncLogLen(data.game.log_len);
          break;
        }
        case "game": {
          // Out-of-band game refresh (seat claims, roster changes).
          setGame(data.game);
          setSnapshot(data.game.snapshot);
          break;
        }
        case "thinking": {
          setThinking({ who: data.who, role: data.role });
          if (thinkingTimeout.current) window.clearTimeout(thinkingTimeout.current);
          // Safety: clear a stuck indicator after a generous window.
          thinkingTimeout.current = window.setTimeout(() => setThinking(null), 90_000);
          break;
        }
        case "tool": {
          setThinking(null);
          setStream((prevStream) => [
            ...prevStream,
            {
              kind: "tool",
              uid: uid("tool"),
              name: data.name,
              input: data.input ?? {},
              result: data.result ?? null,
              ts: Date.now() / 1000,
            },
          ]);
          break;
        }
        case "log": {
          setThinking(null);
          if (thinkingTimeout.current) window.clearTimeout(thinkingTimeout.current);
          // A turn passed — ring a soft tick so the table knows there's new prose to
          // read. Only for readable additions (DM narration + spoken lines), debounced,
          // and skippable by muting. Rolls/system noise and your own typed action don't ring.
          const k = data.entry?.kind;
          if (chimeRef.current && (k === "dm" || k === "narration" || k === "speech")) {
            const now = Date.now();
            if (now - lastTickRef.current > 600) {
              lastTickRef.current = now;
              playBeatTick();
            }
          }
          appendLog(data.entry);
          break;
        }
        case "state": {
          setThinking(null);
          setSnapshot(data.snapshot);
          syncLogLen(data.log_len);
          break;
        }
        case "presence": {
          setPresent(data.present);
          // Presence changes often accompany seat changes (claims, joins);
          // the server broadcasts no seat event today, so refetch the shell.
          refreshGame();
          break;
        }
        case "chat": {
          setChat((prevChat) => [
            ...prevChat.slice(-99),
            { uid: uid("chat"), who: data.who, userId: data.user_id, text: data.text, ts: data.ts },
          ]);
          break;
        }
        case "art": {
          const id = data.kind === "character" ? (data.character_id ?? "")
            : data.kind === "scene" ? "scene" : (data.name ?? "");
          setArt((prevArt) => ({ ...prevArt, [`${data.kind}:${id}`]: data.url }));
          break;
        }
        case "error": {
          setThinking(null);
          const msg = data.message || "Something went wrong on the server.";
          if (/game not found/i.test(msg)) {
            // Terminal: the table no longer exists. Stop reconnecting and
            // hand the user back to the lobby instead of looping forever.
            fatalRef.current = true;
            setGone(true);
            setConn("closed");
            pushToast("This table no longer exists — returning to the lobby.", "error");
            window.location.hash = "#/";
            break;
          }
          // The server broadcasts action errors game-wide, so another
          // player's failure lands here too; drop identical repeats.
          const now = Date.now();
          const last = lastErrorRef.current;
          if (last && last.msg === msg && now - last.ts < ERROR_DEDUP_MS) break;
          lastErrorRef.current = { msg, ts: now };
          setError(msg);
          break;
        }
        default:
          break;
      }
    };

    ws.onerror = () => {
      // onclose handles reconnect; surface nothing noisy here.
    };

    ws.onclose = (ev) => {
      if (wsRef.current === ws) wsRef.current = null;
      if (closedByUs.current || fatalRef.current) {
        setConn("closed");
        return;
      }
      // Deliberate server rejections (policy / application close codes)
      // are terminal — retrying would just hammer the same refusal.
      if (ev.code === 1008 || (ev.code >= 4000 && ev.code < 5000)) {
        fatalRef.current = true;
        setConn("closed");
        setError("The server refused the connection.");
        return;
      }
      retryRef.current += 1;
      const delay = Math.min(1000 * 2 ** (retryRef.current - 1), 15_000);
      setConn("reconnecting");
      reconnectTimer.current = window.setTimeout(connect, delay);
    };
  }, [gameId, appendLog, syncLogLen, refreshGame, pushToast]);

  const reconnect = useCallback(() => {
    retryRef.current = 0;
    fatalRef.current = false;
    setGone(false);
    connect(); // connect() tears down any existing socket first
  }, [connect]);

  useEffect(() => {
    // Reset per-game state when switching games.
    seenLogIds.current = new Set();
    knownLogLen.current = 0;
    logFetchBusy.current = false;
    fatalRef.current = false;
    lastErrorRef.current = null;
    streamingIds.current = new Set();
    lastTurnCharRef.current = null;
    notifiedRollsRef.current = new Set();
    lastAwaitingRef.current = null;
    stopTitleFlash();
    setTurnToast(null);
    setStream([]);
    setGame(null);
    setSnapshot(null);
    setThinking(null);
    setPresent([]);
    setChat([]);
    setArt({});
    setGone(false);
    setError(null);
    retryRef.current = 0;
    if (!gameId) return;
    connect();
    return () => {
      closedByUs.current = true;
      if (reconnectTimer.current) window.clearTimeout(reconnectTimer.current);
      if (thinkingTimeout.current) window.clearTimeout(thinkingTimeout.current);
      if (turnToastTimer.current) window.clearTimeout(turnToastTimer.current);
      stopTitleFlash();
      if (wsRef.current) {
        wsRef.current.onclose = null;
        wsRef.current.close();
        wsRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameId]);

  // Keepalive ping every 25s.
  useEffect(() => {
    const t = window.setInterval(() => {
      const ws = wsRef.current;
      if (ws && ws.readyState === WebSocket.OPEN) {
        try {
          ws.send(JSON.stringify({ action: "ping" }));
        } catch {
          /* ignore */
        }
      }
    }, 25_000);
    return () => window.clearInterval(t);
  }, []);

  // Seat claims/releases arrive over REST with no broadcast today, so a slow
  // poll keeps ownership-derived UI (roll gating, "Acting as") from running
  // on stale seats indefinitely.
  useEffect(() => {
    if (!gameId) return;
    const t = window.setInterval(() => {
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) refreshGame();
    }, 45_000);
    return () => window.clearInterval(t);
  }, [gameId, refreshGame]);

  // Turn / roll-called nudge. Edge-triggered off snapshot changes: when the combat
  // turn pointer lands on a PC this client controls, or the DM calls for a roll from
  // one of ours, fire a nudge (toast + tab-title flash + optional chime). We only
  // notify for seats YOU own — never for other players' or AI turns.
  useEffect(() => {
    if (!snapshot || !you || !game) return;
    const mine = new Set(
      game.seats.filter((s) => s.user_id === you.id && s.character_id).map((s) => s.character_id),
    );
    if (mine.size === 0) return;
    // Whose turn is it? (combat order's current combatant → its character id)
    const current = snapshot.combat?.order.find((c) => c.is_current);
    const curId = current?.character_id ?? null;
    if (curId && curId !== lastTurnCharRef.current && mine.has(curId)) {
      fireTurnNudge(`Your turn — ${current?.name ?? "you"}`);
    }
    lastTurnCharRef.current = curId;
    // A fresh pending roll for one of ours (fire once per roll id).
    for (const pr of snapshot.pending_rolls ?? []) {
      if (mine.has(pr.character_id) && !notifiedRollsRef.current.has(pr.id)) {
        notifiedRollsRef.current.add(pr.id);
        fireTurnNudge(`The DM calls for a roll — ${pr.character_name || current?.name || "you"}`);
      }
    }
    // Conductor spotlight (out of combat): the DM hands the baton to a character we
    // control. Edge-triggered off `awaiting` so it fires once when the baton lands, and
    // only when NOT in combat (combat's turn pointer above owns nudges there).
    const aw = snapshot.awaiting ?? null;
    if (!snapshot.combat && aw && aw !== lastAwaitingRef.current && mine.has(aw)) {
      const name =
        game.seats.find((s) => s.character_id === aw)?.character?.name
        ?? snapshot.party.find((p) => p.id === aw)?.name
        ?? "you";
      fireTurnNudge(`Your turn — ${name}`);
    }
    lastAwaitingRef.current = aw;
  }, [snapshot, you, game, fireTurnNudge]);

  const send = useCallback((msg: WsClientMessage): boolean => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) {
      setError("Not connected — your action wasn't sent. Reconnecting…");
      return false;
    }
    try {
      ws.send(JSON.stringify(msg));
      return true;
    } catch (e) {
      setError(`Failed to send: ${String(e)}`);
      return false;
    }
  }, []);

  return {
    conn,
    game,
    snapshot,
    stream,
    thinking,
    error,
    you,
    present,
    chat,
    art,
    gone,
    clearError,
    send,
    reconnect,
    refreshGame,
    turnToast,
    dismissTurnToast,
    chimeEnabled,
    setChimeEnabled,
  };
}
